'use client';

// biome-ignore lint/style/noRestrictedImports: AppLink owns the unlocalized internal-link escape hatch.
import { c as _c } from "react/compiler-runtime";
import NextLink from 'next/link';
import { useSearchParams } from 'next/navigation';
import React, { type ComponentProps, Suspense, useRef } from 'react';
import AppLinkSSRFallback from '@/components/elementary/AppLink/SSRAppLink';
import type { AppLinkProps } from '@/components/elementary/AppLink/types';
import { buildInternalHref, buildLinkClassName, createQueryString, determineLinkVariant, getAnchorHref, getLinkExceptions, getLiveTrackingProps, renderContent, resolveAppLinkFields } from '@/components/elementary/AppLink/utils';
import { Link as IntlLink, type LinkProps } from '@/i18n/navigation';
import { flushAnalytics, formatEventName, useAnalytics } from '@/lib/analytics/analytics';
import { ANALYTICS_EVENTS } from '@/lib/analytics/analytics.constants';
import { useComponentTracking } from '@/lib/analytics/component-tracking';
import { getOriginUrl } from '@/lib/general-helpers';
import { isAppLink } from '@/utils/links';
const AppLinkComponent = React.forwardRef<HTMLAnchorElement, AppLinkProps>(({
  href: hrefProp,
  link,
  label: labelProp,
  className,
  children,
  iconEnd,
  iconStart,
  openExternalInNewTab: openExternalInNewTabProp,
  hrefMode: hrefModeProp,
  forceAnchor = false,
  size = 'none',
  inverted,
  underline = false,
  trackingContext,
  onClick,
  ...props
}, ref) => {
  const searchParams = useSearchParams();
  const {
    track
  } = useAnalytics();
  const componentTracking = useComponentTracking();
  const pointerDownTrackedRef = useRef(false);
  const resolvedLink = resolveAppLinkFields({
    href: hrefProp,
    link,
    label: labelProp,
    openExternalInNewTab: openExternalInNewTabProp,
    hrefMode: hrefModeProp,
    props
  });
  if (!resolvedLink) return null;
  const {
    href,
    label,
    hrefMode,
    openExternalInNewTab,
    props: resolvedProps
  } = resolvedLink;
  const variant = determineLinkVariant(href, label || children?.toString(), resolvedProps.variant);
  const linkClassName = buildLinkClassName({
    variant,
    size,
    inverted,
    className,
    underline
  });
  const linkException = getLinkExceptions({
    href,
    ref,
    linkClassName,
    props: resolvedProps
  }, () => renderContent({
    variant,
    iconStart,
    iconEnd,
    label,
    children
  }));
  if (linkException) {
    return linkException;
  }
  const isExternal = typeof href === 'string' && !isAppLink(href);
  const trackingFallback = {
    href,
    label,
    children
  };

  // Track on pointerdown for external links — eg. knock.ai widgets (start-chat)
  // swallow click events via stopPropagation, so onClick never fires.
  const handlePointerDown = (event: React.PointerEvent<HTMLAnchorElement>) => {
    if (event.button !== 0) return;
    track({
      eventType: formatEventName(ANALYTICS_EVENTS.LINK_CLICKED),
      eventProperties: {
        ...getLiveTrackingProps(event.currentTarget, trackingFallback),
        ...(componentTracking && {
          component: componentTracking.component
        }),
        ...trackingContext
      }
    });
    pointerDownTrackedRef.current = true;
  };
  const handleExternalClick = (event_0: React.MouseEvent<HTMLAnchorElement>) => {
    // Track only if pointerdown didn't already (keyboard navigation case)
    if (!pointerDownTrackedRef.current) {
      track({
        eventType: formatEventName(ANALYTICS_EVENTS.LINK_CLICKED),
        eventProperties: {
          ...getLiveTrackingProps(event_0.currentTarget, trackingFallback),
          ...(componentTracking && {
            component: componentTracking.component
          }),
          ...trackingContext
        }
      });
    }
    pointerDownTrackedRef.current = false;
    flushAnalytics();
    onClick?.(event_0);
  };
  const handleClick = (event_1: React.MouseEvent<HTMLAnchorElement>) => {
    track({
      eventType: formatEventName(ANALYTICS_EVENTS.LINK_CLICKED),
      eventProperties: {
        ...getLiveTrackingProps(event_1.currentTarget, trackingFallback),
        ...(componentTracking && {
          component: componentTracking.component
        }),
        ...trackingContext
      }
    });
    onClick?.(event_1);
  };
  if (isExternal || forceAnchor) {
    return <a href={getAnchorHref(href, isExternal, createQueryString(searchParams))} ref={ref} target={openExternalInNewTab ? '_blank' : ''} rel={openExternalInNewTab ? 'noopener noreferrer' : ''} className={linkClassName} onPointerDown={handlePointerDown} onClick={handleExternalClick} {...resolvedProps}>
          {renderContent({
        variant,
        iconStart,
        iconEnd,
        label,
        children
      })}
        </a>;
  }
  const linkHref = (typeof href === 'string' ? href.startsWith('#') ? href : buildInternalHref(href, searchParams, getOriginUrl()) : href) as LinkProps['href'];
  if (hrefMode === 'unlocalized') {
    const {
      locale: _locale,
      ...unlocalizedProps
    } = resolvedProps;
    return <NextLink href={linkHref as ComponentProps<typeof NextLink>['href']} ref={ref} className={linkClassName} onClick={handleClick} {...unlocalizedProps}>
          {renderContent({
        variant,
        iconStart,
        iconEnd,
        label,
        children
      })}
        </NextLink>;
  }
  return <IntlLink href={linkHref} ref={ref} className={linkClassName} onClick={handleClick} {...resolvedProps}>
        {renderContent({
      variant,
      iconStart,
      iconEnd,
      label,
      children
    })}
      </IntlLink>;
});
const AppLinkDefaultComponent = React.forwardRef<HTMLAnchorElement, AppLinkProps>((props, ref) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== props || $[1] !== ref) {
    t0 = <Suspense fallback={<AppLinkSSRFallback {...props} ref={ref} />}><AppLinkComponent {...props} ref={ref} /></Suspense>;
    $[0] = props;
    $[1] = ref;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
});
export default AppLinkDefaultComponent;